/**
 * Event Type Selector Component
 */

import React from 'react';
import { Autocomplete, TextField, Chip, Box, Typography } from '@mui/material';
import { useEventCatalog } from '../hooks/useWorkflowChaining';
import { getModuleColors } from '../utils/moduleColors';

interface EventTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

export const EventTypeSelector: React.FC<EventTypeSelectorProps> = ({
  value,
  onChange,
  error,
  helperText,
}) => {
  const { data: events, isLoading } = useEventCatalog();

  const selectedEvent = events?.find((e) => e.fullType === value || e.type === value);

  return (
    <Autocomplete
      value={selectedEvent || null}
      onChange={(_, newValue) => onChange(newValue?.fullType || '')}
      options={events || []}
      groupBy={(option) => option.module}
      getOptionLabel={(option) => option.type}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Event Type"
          error={error}
          helperText={helperText || 'Select an event that will trigger this workflow'}
          required
        />
      )}
      renderOption={(props, option) => {
        const colors = getModuleColors(option.module);
        return (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {option.type}
                </Typography>
                <Chip
                  label={option.module}
                  size="small"
                  sx={{
                    backgroundColor: colors.border,
                    color: 'white',
                    height: 18,
                    fontSize: '0.65rem',
                  }}
                />
                <Chip
                  label={option.kind}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Box>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
    />
  );
};

